import {Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import Button from '@mui/material/Button';
import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {BrickContext, Config} from 'olympe';
import {getRequestCockpitLink} from '../../../utils/helpers';
import {TablePaginationWithPageSelector} from '../../TablePaginationWithPageSelector.jsx';
import {tagToDisplayER} from '../../../utils/tagTransforms';
import {getMailContentPRToApprove} from '../../../utils/email';
import {
    getEmailByUserIdGraphAPILambda,
    saveWithoutChangeLogLambda,
    sendEmailLambda,
    toErrorFlow,
} from '../../../utils/functionSignatures';
import {PurchaseRequest} from '../../../models/PurchaseRequest';
import {createPRJsonWithSubmissionDate} from '../../../utils/createPRJson';
import LoadingButton from '@mui/lab/LoadingButton';
import {ErrorPopup} from './ErrorPopup.jsx';
import {LAMBDA_NAMES} from '../../../utils/constants';

/**
 * This component display a popup with the list of mass submitted PRs sent to SAP, to requester or for SB approval.
 * @param {MassSubmitSuccessPopup.propTypes} props
 * @return {JSX.Element}
 */
export function MassSubmitSuccessPopup(props) {
    const redirectLink = getRequestCockpitLink(Config.getParameter('ENV'));

    const [requesterPage, setRequesterPage] = useState(0);
    const [sbApprovalPage, setSbApprovalPage] = useState(0);
    const [sapPage, setSapPage] = useState(0);

    const [requesterRowsPerPage, setRequesterRowsPerPage] = useState(5);
    const [sbApprovalRowsPerPage, setSbApprovalRowsPerPage] = useState(5);
    const [sapRowsPerPage, setSapRowsPerPage] = useState(5);

    const [requesterPageNum, setRequesterPageNum] = useState([]);
    const [sbApprovalPageNum, setSbApprovalPageNum] = useState([]);
    const [sapPageNum, setSapPageNum] = useState([]);

    const requesterPageSize = requesterRowsPerPage === -1 ? props.prsSubmittedToRequester.length : (props.prsSubmittedToRequester.length) / requesterRowsPerPage;
    const sbApprovalPageSize = sbApprovalRowsPerPage === -1 ? props.prsSBApprovalNeeded.length : (props.prsSBApprovalNeeded.length) / sbApprovalRowsPerPage;
    const sapPageSize = sapRowsPerPage === -1 ? props.prs.length : (props.prs.length) / sapRowsPerPage;

    const getUserIdGraphAPI = props.context.get(LAMBDA_NAMES.GET_USER_ID_GRAPH_API);
    const sendEmail = props.context.get(LAMBDA_NAMES.SEND_EMAIL);
    const savePR = props.context.get(LAMBDA_NAMES.SAVE);

    const multiplePRs = props.prsSBApprovalNeeded.length > 1;

    const [approversBeingFetched, setApproversBeingFetched] = useState(false);
    const [confirmSBValidationLoading, setConfirmSBValidationLoading] = useState(false);

    const [errorPopupOpen, setErrorPopupOpen] = useState(false);
    const [errorPopupMsg, setErrorPopupMsg] = useState([]);

    const openErrorPopup = (msg) => {
        setApproversBeingFetched(false);
        setErrorPopupMsg([msg]);
        setErrorPopupOpen(true);
    };
    const handleCloseErrorPopup = () => {
        props.context.set('Failed PRs', props.failedMassSubmittedPRs);
        setErrorPopupOpen(false);
        props.context.trigger('Refresh');
    };
    const handleCancel = () => {
        props.context.set('Failed PRs', props.failedMassSubmittedPRs);
        props.setSbApprovalNeededMassSubmittedPRs([]);
        props.setOpen(false);
        props.context.trigger('Refresh');
    };

    const sendEmailAndSave = (purchaseRequest, reasonsForApproval, emailRecipients, env, prTag) => {
        if (emailRecipients) {
            const EPRid = purchaseRequest.EPRId || tagToDisplayER(purchaseRequest.tag);
            const now = new Date();
            const readableDateOptions = {month: 'short', day: 'numeric', year: 'numeric'};
            const readableSubmissionDate = now.toLocaleDateString('en-US', readableDateOptions);
            const mailContent = getMailContentPRToApprove(reasonsForApproval.length, reasonsForApproval, purchaseRequest.tag, EPRid, purchaseRequest.requester, purchaseRequest.vendorCode, purchaseRequest.vendorName, purchaseRequest.projectTitle, readableSubmissionDate);
            const requesterLabel = purchaseRequest.requester?.label ?? '';
            const vendorNameValue = purchaseRequest.vendorName ?? '';
            const subject = `[Easy Request] ${env !== 'prod' ? (env === 'codev' ? '[DEV] ' : '[PRE-PROD] ') : ''}PR to check! ${EPRid}${requesterLabel ? ` | ${requesterLabel}` : ''}${vendorNameValue ? ` | ${vendorNameValue}` : ''}`;

            return sendEmailLambda(props.context, sendEmail, emailRecipients, '', '', subject, mailContent, undefined);
        }
    };

    const handleCloseSB = (event, reason) => {
        setConfirmSBValidationLoading(true);

        // User canceled via outside click or Escape button
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            props.context.set('Failed PRs', props.failedMassSubmittedPRs);
            props.setSbApprovalNeededMassSubmittedPRs([]);
            setConfirmSBValidationLoading(false);
            props.setOpen(false);
            props.context.trigger('Refresh');
            return;
        }

        const env = Config.getParameter('ENV');

        const sBApproversSet = new Set();
        props.prsSBApprovalNeeded.forEach((pr) => {
            pr.sBApprovers.split(';').forEach((email) => {
                sBApproversSet.add(email.trim().toLowerCase());
            });
        });

        const sBApproversArray = Array.from(sBApproversSet);

        getEmailByUserIdGraphAPILambda(props.context, getUserIdGraphAPI, sBApproversArray)
            .then(async (userIds) => {
                const parsedResultOut = JSON.parse(userIds[0]);
                const result = parsedResultOut.responses.flatMap((row) => row.body.value).map((row) => ({
                    label: `${row['givenName']} ${row['surname']}`,
                    value: row['onPremisesSamAccountName'],
                    email: row['mail'],
                }));

                if (env === 'preprod') {
                    result.push(
                        {label: 'Amara Vani Adapa', value: 'CAADAPAAM', email: 'amaravani.adapa@ca.nestle.com'},
                        {label: 'Kumaresan Balasubramanian', value: 'AUKUMAREBA', email: 'balasubramanian.kumaresan@aoa.nestle.com'},
                        {label: 'Hoffmann Achim', value: 'DEHOFFMAAC', email: 'achim.hoffmann@eur.nestle.com'},
                    );
                }

                const resultMap = result.reduce((map, obj) => {
                    map[obj.email] = obj;
                    return map;
                }, {});

                for (const pr of props.prsSBApprovalNeeded) {
                    const purchaseRequest = new PurchaseRequest(JSON.stringify(pr.prJson));
                    const sBApproversEmails = pr.sBApprovers.split(';').map((email) => email.trim().toLowerCase());
                    const strategicBuyers = sBApproversEmails.map((email) => resultMap[email]).filter(Boolean);

                    if (strategicBuyers.length !== sBApproversEmails.length) {
                        openErrorPopup(`The following strategic buyer(s) is not in the database: ${pr.sBApprovers}. Please contact your administrator`);
                    } else {
                        purchaseRequest.sbValidation.strategicBuyers = strategicBuyers;
                        purchaseRequest.status = PurchaseRequest.statuses.pendingSB;
                        try {
                            await sendEmailAndSave(purchaseRequest, pr.reasonsForApproval, pr.sBApprovers, env, pr.prTag);
                            try {
                                await saveWithoutChangeLogLambda(props.context, savePR, pr.prTag, createPRJsonWithSubmissionDate(purchaseRequest));
                            } catch (e) {
                                openErrorPopup('Something went wrong while saving the PR.');
                            }
                            setApproversBeingFetched(false);
                        } catch (e) {
                            openErrorPopup(`Something went wrong while sending email to strategic buyers. ${toErrorFlow(e).getMessage()}`);
                        }
                    }
                }

                props.context.set('Failed PRs', props.failedMassSubmittedPRs);
                props.setSbApprovalNeededMassSubmittedPRs([]);
                setConfirmSBValidationLoading(false);
                props.setOpen(false);
                props.context.trigger('Refresh');
            })
            .catch((reason) => {
                openErrorPopup(`Something went wrong while fetching SB User IDs. ${toErrorFlow(reason).getMessage()}`);
            });
    };

    const handleRequesterPageSelection = (newValue) => {
        setRequesterPage(newValue);
    };

    const handleRequesterChangePage = (event, newPage) => {
        setRequesterPage(newPage);
    };

    const handleRequesterChangeRowsPerPage = (event) => {
        setRequesterRowsPerPage(+event.target.value);
        setRequesterPage(0);
    };

    const handleSbApprovalPageSelection = (newValue) => {
        setSbApprovalPage(newValue);
    };

    const handleSbApprovalChangePage = (event, newPage) => {
        setSbApprovalPage(newPage);
    };

    const handleSbApprovalChangeRowsPerPage = (event) => {
        setSbApprovalRowsPerPage(+event.target.value);
        setSbApprovalPage(0);
    };

    const handleSapPageSelection = (newValue) => {
        setSapPage(newValue);
    };

    const handleSapChangePage = (event, newPage) => {
        setSapPage(newPage);
    };

    const handleSapChangeRowsPerPage = (event) => {
        setSapRowsPerPage(+event.target.value);
        setSapPage(0);
    };

    useEffect(() => {
        const temp = [];
        if (requesterRowsPerPage === -1) {
            temp.push(
                {
                    label: `1-${props.prsSubmittedToRequester.length}`,
                    value: 1,
                },
            );
        } else {
            for (let i = 0; i < requesterPageSize; i++) {
                temp.push({
                    label: `${(i * requesterRowsPerPage) + 1}-${(i + 1) * requesterRowsPerPage < props.prsSubmittedToRequester.length ? (i + 1) * requesterRowsPerPage : props.prsSubmittedToRequester.length}`,
                    value: i + 1,
                });
            }
        }
        return setRequesterPageNum(temp);
    }, [requesterPageSize]);

    useEffect(() => {
        const temp = [];
        if (sbApprovalRowsPerPage === -1) {
            temp.push(
                {
                    label: `1-${props.prsSBApprovalNeeded.length}`,
                    value: 1,
                },
            );
        } else {
            for (let i = 0; i < sbApprovalPageSize; i++) {
                temp.push({
                    label: `${(i * sbApprovalRowsPerPage) + 1}-${(i + 1) * sbApprovalRowsPerPage < props.prsSBApprovalNeeded.length ? (i + 1) * sbApprovalRowsPerPage : props.prsSBApprovalNeeded.length}`,
                    value: i + 1,
                });
            }
        }
        return setSbApprovalPageNum(temp);
    }, [sbApprovalPageSize]);

    useEffect(() => {
        const temp = [];
        if (sapRowsPerPage === -1) {
            temp.push(
                {
                    label: `1-${props.prs.length}`,
                    value: 1,
                },
            );
        } else {
            for (let i = 0; i < sapPageSize; i++) {
                temp.push({
                    label: `${(i * sapRowsPerPage) + 1}-${(i + 1) * sapRowsPerPage < props.prs.length ? (i + 1) * sapRowsPerPage : props.prs.length}`,
                    value: i + 1,
                });
            }
        }
        return setSapPageNum(temp);
    }, [sapPageSize]);

    const paginatedRequesterPRs = props.prsSubmittedToRequester.slice(requesterPage * requesterRowsPerPage, requesterPage * requesterRowsPerPage + requesterRowsPerPage);
    const paginatedSBApprovalPRs = props.prsSBApprovalNeeded.slice(sbApprovalPage * sbApprovalRowsPerPage, sbApprovalPage * sbApprovalRowsPerPage + sbApprovalRowsPerPage);
    const paginatedSapPRs = props.prs.slice(sapPage * sapRowsPerPage, sapPage * sapRowsPerPage + sapRowsPerPage);

    return (
        <>
            <Dialog
                onClose={handleCancel}
                open={props.open}
                aria-labelledby="mass-submit-popup"
            >
                <div style={{display: 'flex'}}>
                    <div style={{backgroundColor: '#008000', width: '20%', height: 'auto', display: 'flex', alignItems: 'center', minWidth: '50px', justifyContent: 'center'}}>
                        <i className="material-icons" style={{color: 'white'}}>{'check_circle'}</i>
                    </div>
                    <div>
                        <DialogTitle id="popup-title">Multiple requests have been submitted</DialogTitle>

                        <DialogContent className='popup-scroller'
                            sx={{
                                maxHeight: '400px !important',
                                overflowY: 'scroll !important',
                                minWidth: '30rem',
                            }}>
                            {props.prsSubmittedToRequester.length > 0 && (
                                <>
                                    <DialogContentText sx={{color: 'black'}}>
                                        The following requests have been submitted to the Requester:
                                    </DialogContentText>
                                    <ul>
                                        {paginatedRequesterPRs.map(
                                            (pr, idx) => (
                                                <li key={`${pr.EPRID}`}>
                                                    <DialogContentText id="list-of-submitted-pr-to-requester">
                                                        {`EPR ID: ${pr.EPRID}`}
                                                    </DialogContentText>
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                    <div style={{marginBottom: '5rem'}}>
                                        <TablePaginationWithPageSelector
                                            page={requesterPage}
                                            pageNum={requesterPageNum}
                                            rowsPerPage={requesterRowsPerPage}
                                            handleChangeRowsPerPage={handleRequesterChangeRowsPerPage}
                                            handleChangePage={handleRequesterChangePage}
                                            handlePageSelection={handleRequesterPageSelection}
                                            count={props.prsSubmittedToRequester.length}
                                            rowsPerPageOptions={[2, 5, 10, 20, 50, 100, 200]}
                                            textColor="rgba(0, 0, 0, 0.6)"
                                        />
                                    </div>
                                    <br/>
                                </>
                            )}

                            {props.prsSBApprovalNeeded.length > 0 && (
                                <>
                                    <DialogContentText sx={{color: 'black'}}>
                                        {`The following request${multiplePRs ? 's' : ''} will be sent to a Strategic Buyer, pending an approval:`}
                                    </DialogContentText>
                                    <ul>
                                        {paginatedSBApprovalPRs.map(
                                            (pr, idx) => (
                                                <li key={`${pr.EPRID}-${idx}-massSubmitApprovalNeeded`}>
                                                    <DialogContentText id="list-of-pr-needing-approval">
                                                        {`EPR-${pr.EPRID}`}
                                                    </DialogContentText>
                                                    <ul>
                                                        {pr.reasonsForApproval && pr.reasonsForApproval.map(
                                                            (reason, reasonIdx) => (
                                                                <li key={`${pr.EPRID}-${idx}-${reasonIdx}-reason`}>
                                                                    <DialogContentText id="list-of-reasons-for-approval">
                                                                        {reason}
                                                                    </DialogContentText>
                                                                </li>
                                                            ),
                                                        )}
                                                    </ul>
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                    <div style={{marginBottom: '5rem'}}>
                                        <TablePaginationWithPageSelector
                                            page={sbApprovalPage}
                                            pageNum={sbApprovalPageNum}
                                            rowsPerPage={sbApprovalRowsPerPage}
                                            handleChangeRowsPerPage={handleSbApprovalChangeRowsPerPage}
                                            handleChangePage={handleSbApprovalChangePage}
                                            handlePageSelection={handleSbApprovalPageSelection}
                                            count={props.prsSBApprovalNeeded.length}
                                            rowsPerPageOptions={[2, 5, 10, 20, 50, 100, 200]}
                                            textColor="rgba(0, 0, 0, 0.6)"
                                        />
                                    </div>
                                    <DialogContentText sx={{marginBottom: '1rem'}}>
                                        {`An email will be sent to the SB indicating they need to check your request${multiplePRs ? 's' : ''}.`}
                                    </DialogContentText>
                                    <br/>
                                </>
                            )}

                            {props.prs.length > 0 && (
                                <>
                                    <DialogContentText sx={{color: 'black'}}>
                                        The following requests have been successfully sent to SAP:
                                    </DialogContentText>
                                    <ul>
                                        {paginatedSapPRs.map((pr, idx) => (
                                            <li key={`${pr.EPRID}-${pr.SAPPRNumber}`}>
                                                <DialogContentText id="list-of-submitted-pr-to-sap">
                                                    {`EPR ID: EPR-${pr.EPRID} -> SAP PR Number: ${pr.SAPPRNumber}`}
                                                </DialogContentText>
                                            </li>
                                        ))}
                                    </ul>
                                    <div style={{marginBottom: '5rem'}}>
                                        <TablePaginationWithPageSelector
                                            page={sapPage}
                                            pageNum={sapPageNum}
                                            rowsPerPage={sapRowsPerPage}
                                            handleChangeRowsPerPage={handleSapChangeRowsPerPage}
                                            handleChangePage={handleSapChangePage}
                                            handlePageSelection={handleSapPageSelection}
                                            count={props.prs.length}
                                            rowsPerPageOptions={[2, 5, 10, 20, 50, 100, 200]}
                                            textColor="rgba(0, 0, 0, 0.6)"
                                        />
                                    </div>
                                    <div>
                                        <DialogContentText>
                                            {`Please refer back to the `}
                                            <a href={redirectLink} target={'_blank'} rel="noreferrer">Request Cockpit</a>
                                            {` for further information and additional actions regarding the request, or close this popup to continue managing your open requests.`}
                                        </DialogContentText>
                                    </div>
                                </>
                            )}
                        </DialogContent>

                        <DialogActions>
                            {props.prsSBApprovalNeeded.length > 0 ? (
                                <>
                                    {approversBeingFetched && (
                                        <span className="popup-action-label">Searching SBs for your company code...</span>
                                    )}
                                    <Button onClick={handleCancel}>Cancel SB validation</Button>
                                    <LoadingButton
                                        data-cy={'Approval_needed_ok_btn'}
                                        onClick={(event, reason) => handleCloseSB(event, reason)}
                                        style={{paddingRight: approversBeingFetched || confirmSBValidationLoading ? '40px' : '15px'}}
                                        loading={approversBeingFetched || confirmSBValidationLoading}
                                        loadingPosition={'end'}
                                        endIcon={<div />}
                                        disabled={approversBeingFetched || confirmSBValidationLoading}
                                    >
                                        Confirm SB validation
                                    </LoadingButton>
                                </>
                            ) : (
                                <Button onClick={handleCancel}>Ok</Button>
                            )}
                        </DialogActions>
                    </div>
                </div>
            </Dialog>
            <ErrorPopup onClose={handleCloseErrorPopup} open={errorPopupOpen} errorMessages={errorPopupMsg}/>
        </>
    );
}

MassSubmitSuccessPopup.propTypes = {
    context: PropTypes.instanceOf(BrickContext).isRequired,
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    prs: PropTypes.arrayOf(PropTypes.object),
    prsSubmittedToRequester: PropTypes.arrayOf(PropTypes.object),
    prsSBApprovalNeeded: PropTypes.arrayOf(PropTypes.object),
    setSbApprovalNeededMassSubmittedPRs: PropTypes.func,
    failedMassSubmittedPRs: PropTypes.array.isRequired,
};
